import { Loader2, Mic, MicOff, PhoneOff, Video, VideoOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { CallState } from "@/types/call";

interface CallControlsProps {
  state: CallState;
  micMuted: boolean;
  cameraOff: boolean;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onEndCall: () => void;
}

/** Mic / camera are local SDK toggles; End Call is the only backend action here. */
export function CallControls({
  state,
  micMuted,
  cameraOff,
  onToggleMic,
  onToggleCamera,
  onEndCall,
}: CallControlsProps) {
  const ending = state === "ending";
  const mediaDisabled = state === "connectingSdk" || ending;

  return (
    <div className="flex flex-wrap items-center justify-center gap-3">
      <Button
        type="button"
        variant={micMuted ? "secondary" : "outline"}
        size="lg"
        className="gap-2"
        onClick={onToggleMic}
        disabled={mediaDisabled}
        aria-pressed={micMuted}
        aria-label={micMuted ? "Unmute microphone" : "Mute microphone"}
      >
        {micMuted ? <MicOff className="size-4" /> : <Mic className="size-4" />}
        {micMuted ? "Microphone muted" : "Microphone on"}
      </Button>

      <Button
        type="button"
        variant={cameraOff ? "secondary" : "outline"}
        size="lg"
        className="gap-2"
        onClick={onToggleCamera}
        disabled={mediaDisabled}
        aria-pressed={cameraOff}
        aria-label={cameraOff ? "Turn camera on" : "Turn camera off"}
      >
        {cameraOff ? <VideoOff className="size-4" /> : <Video className="size-4" />}
        {cameraOff ? "Camera off" : "Camera on"}
      </Button>

      <Button
        type="button"
        variant="destructive"
        size="lg"
        className="gap-2"
        onClick={onEndCall}
        disabled={ending}
        aria-label="End call"
      >
        {ending ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <PhoneOff className="size-4" aria-hidden />
        )}
        {ending ? "Ending…" : "End call"}
      </Button>
    </div>
  );
}
